// src/lib/hooks/usePauseEmbedOnHidden.ts
'use client'

import { type RefObject } from 'react'
import { useIsomorphicLayoutEffect } from '~/lib/hooks/useIsomorphicLayoutEffect'

/**
 * Stop a cross-origin video `<iframe>` (YouTube/Vimeo) from playing while its
 * route is hidden by Cache Components' `<Activity>`. `display: none` does not
 * stop iframe playback and there is no cross-origin `.pause()` API, so the only
 * reliable lever is to blank the iframe's `src` on hide and restore it on show.
 *
 * The caller renders `<iframe src={src} ref={ref}>` NORMALLY so the embed is
 * present in the SSR HTML and works without JS (a progressive-enhancement win).
 * On mount the effect body sees `src` already correct and no-ops; it only blanks
 * to `about:blank` on hide and restores `src` on show. Because `src` is stable,
 * React never re-touches the attribute after mount, so these imperative writes
 * don't fight reconciliation — and doing it through the ref (rather than an
 * unmount-via-`useState`) avoids a `setState`-in-effect. Restoring `src` reloads
 * the embed from the start on return (cross-origin playback position can't be
 * preserved — and an autoplay embed, e.g. `EnhancedVideoPlayer autoplay`, will
 * re-autoplay from 0 on return); the trade is intentional: audio stopping when
 * the user navigates away matters more than resume position, and there's no
 * better cross-origin lever short of the provider postMessage APIs. Don't
 * "fix" the reload — it's the point.
 */
export function usePauseEmbedOnHidden(
  ref: RefObject<HTMLIFrameElement | null>,
  src: string,
): void {
  useIsomorphicLayoutEffect(() => {
    // Effect body runs on first mount AND on every hide→visible transition. On
    // mount the SSR-rendered `src` already matches, so this no-ops; on re-show it
    // restores the URL the cleanup blanked.
    const el = ref.current
    if (el && el.getAttribute('src') !== src) {
      el.setAttribute('src', src)
    }

    // Cleanup runs synchronously on hide (and unmount): blank the frame so the
    // third-party player tears down and audio stops.
    return () => {
      const node = ref.current
      if (node) node.setAttribute('src', 'about:blank')
    }
  }, [ref, src])
}
